import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Plus } from "lucide-react";
import { useResource } from "../../hooks/useResource.js";
import { DataTable } from "../data-table/DataTable.jsx";
import { Pagination } from "../data-table/Pagination.jsx";
import { FilterBar } from "../data-table/FilterBar.jsx";
import { Button } from "../ui/Button.jsx";
import { Modal } from "../ui/Modal.jsx";
import { ConfirmDialog } from "../ui/ConfirmDialog.jsx";
import { RowActions } from "../ui/RowActions.jsx";
import { Input } from "../ui/Input.jsx";
import { Textarea } from "../ui/Textarea.jsx";
import { Select } from "../ui/Select.jsx";
import { Switch } from "../ui/Switch.jsx";
import { SlugField } from "../forms/SlugField.jsx";
import { ImageUpload } from "../upload/ImageUpload.jsx";
import { PdfUpload } from "../upload/PdfUpload.jsx";

/**
 * Config-driven list + modal-form CRUD page used by every simple resource
 * (Collections, Categories, Awards, Team, Testimonials, Matterport, etc.).
 *
 * Bespoke pages (Designs, Blogs, Facades) bypass this and roll their own.
 *
 * Config shape:
 *   {
 *     resource: "awards",                  // backend slug
 *     resourcePath: "awards",              // frontend path slug (defaults to resource)
 *     title: "Awards",
 *     section: "About",                    // breadcrumb / overview chip
 *     newLabel: "New award",
 *     defaultValues: { year: 2025 },
 *     columns: [{ key, label, render?, sortable? }, ...],
 *     fields: [{ name, label, type, ... }, ...],
 *     buildPayload?: (values) => values,   // optional pre-submit transform
 *     describe?: (row) => "title text",    // confirm-delete description
 *     listParams?: () => params object,    // optional default list query params
 *     searchPlaceholder?: string,
 *   }
 */
export function GenericCrudPage({ config }) {
  const {
    resource,
    title,
    section,
    newLabel = "Create",
    defaultValues = {},
    columns,
    fields,
    buildPayload = (v) => v,
    describe = (row) => row.name ?? row.title ?? row.customerName ?? "this item",
    listParams = () => ({}),
    searchPlaceholder = "Search…",
    pageSize = 24,
  } = config;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // null | { _id?: ... } (new) | row (edit)
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    document.title = `${title} · Admin`;
  }, [title]);

  const { list, create, update, remove } = useResource(resource);
  const params = {
    page,
    limit: pageSize,
    ...(search && { search }),
    ...listParams(),
  };
  const { data, isLoading } = list(params);

  useEffect(() => {
    setPage(1);
  }, [search]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          {section && <p className="text-xs uppercase tracking-widest text-accent">{section}</p>}
          <h1 className="mt-1 font-display text-2xl text-text-primary">{title}</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {data?.meta?.total ?? "—"} item{data?.meta?.total === 1 ? "" : "s"}
          </p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing({})}>
          {newLabel}
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-border-subtle">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={searchPlaceholder}
        />
        <DataTable
          loading={isLoading}
          rows={data?.items ?? []}
          columns={columns}
          actions={(row) => (
            <RowActions onEdit={() => setEditing(row)} onDelete={() => setConfirmDelete(row)} />
          )}
          empty={{
            title: search ? "No matches" : `No ${title.toLowerCase()} yet`,
            description: search
              ? "Try clearing the search above."
              : `Click "${newLabel}" to add the first item.`,
          }}
        />
        <Pagination meta={data?.meta} onChange={setPage} />
      </div>

      {editing && (
        <ResourceFormModal
          row={editing._id ? editing : null}
          fields={fields}
          defaultValues={defaultValues}
          title={editing._id ? `Edit ${describe(editing)}` : newLabel}
          onClose={() => setEditing(null)}
          onSubmit={async (values) => {
            const payload = buildPayload(values);
            if (editing._id) {
              await new Promise((resolve, reject) =>
                update.mutate(
                  { id: editing._id, data: payload },
                  {
                    onSuccess: () => {
                      setEditing(null);
                      resolve();
                    },
                    onError: reject,
                  }
                )
              );
            } else {
              await new Promise((resolve, reject) =>
                create.mutate(payload, {
                  onSuccess: () => {
                    setEditing(null);
                    resolve();
                  },
                  onError: reject,
                })
              );
            }
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title={`Delete "${describe(confirmDelete ?? {})}"?`}
        description="This action cannot be undone."
        confirmLabel="Delete"
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(confirmDelete._id, {
            onSuccess: () => setConfirmDelete(null),
          });
        }}
      />
    </div>
  );
}

/* ============================================================================
   Modal form rendered from a fields config
   ============================================================================ */

function flattenObjectFields(values, fields) {
  // For nested fields like "socialLinks.linkedin", initialize the nested obj
  // so RHF doesn't complain about uncontrolled inputs. Recurses into "row"
  // blocks (which group two fields side-by-side) and ignores anything
  // without a `name` so layout-only entries can't crash the form.
  const out = { ...values };
  const walk = (list) => {
    for (const f of list ?? []) {
      if (!f) continue;
      if (f.type === "row" && Array.isArray(f.fields)) {
        walk(f.fields);
        continue;
      }
      if (typeof f.name !== "string") continue;
      if (f.name.includes(".")) {
        const [parent, child] = f.name.split(".");
        out[parent] = { ...(out[parent] ?? {}), [child]: out[parent]?.[child] ?? "" };
      }
    }
  };
  walk(fields);
  return out;
}

function ResourceFormModal({ row, fields, defaultValues, title, onClose, onSubmit }) {
  const initial = flattenObjectFields({ ...defaultValues, ...(row ?? {}) }, fields);
  const form = useForm({ defaultValues: initial });
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = form;

  const submit = handleSubmit(onSubmit);

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={submit} loading={isSubmitting}>
            {row ? "Save changes" : "Create"}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {fields.map((f, i) => (
          <FieldRenderer
            key={f.name ?? `row-${i}`}
            field={f}
            register={register}
            control={control}
            errors={errors}
            watch={watch}
            setValue={setValue}
          />
        ))}
        {/* Hidden submit so Enter key triggers form submission. */}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

function FieldRenderer({ field, register, control, errors, watch, setValue }) {
  // Layout-only blocks (e.g. "row") have no `name`, so guard the lookup.
  // Without this, opening a form with row groups crashes before the switch
  // statement below can dispatch on field.type.
  const error =
    typeof field.name === "string"
      ? field.name.split(".").reduce((acc, k) => acc?.[k], errors)?.message ?? null
      : null;

  switch (field.type) {
    case "text":
    case "email":
    case "url":
    case "number":
      return (
        <Input
          type={field.type === "number" ? "number" : field.type}
          label={field.label}
          required={field.required}
          hint={field.hint}
          error={error}
          step={field.step}
          min={field.min}
          max={field.max}
          {...register(field.name, {
            required: field.required ? `${field.label} is required` : false,
            valueAsNumber: field.type === "number",
          })}
        />
      );
    case "textarea":
      return (
        <Textarea
          label={field.label}
          rows={field.rows ?? 3}
          required={field.required}
          hint={field.hint}
          error={error}
          {...register(field.name, {
            required: field.required ? `${field.label} is required` : false,
          })}
        />
      );
    case "select":
      return (
        <Select
          label={field.label}
          required={field.required}
          hint={field.hint}
          error={error}
          {...register(field.name, {
            required: field.required ? `${field.label} is required` : false,
          })}
        >
          {field.placeholder && <option value="">{field.placeholder}</option>}
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      );
    case "switch":
      return (
        <Controller
          control={control}
          name={field.name}
          render={({ field: ctrl }) => (
            <Switch
              label={field.label}
              hint={field.hint}
              checked={Boolean(ctrl.value)}
              onCheckedChange={ctrl.onChange}
            />
          )}
        />
      );
    case "slug":
      return (
        <SlugField
          value={watch(field.name) ?? ""}
          onChange={(v) => setValue(field.name, v)}
          source={watch(field.source ?? "name") ?? ""}
          label={field.label ?? "Slug"}
          hint={field.hint}
          error={error}
        />
      );
    case "image":
      return (
        <Controller
          control={control}
          name={field.name}
          render={({ field: ctrl }) => (
            <ImageUpload
              label={field.label}
              hint={field.hint}
              required={field.required}
              aspect={field.aspect ?? "video"}
              subdir={field.subdir}
              value={ctrl.value}
              onChange={ctrl.onChange}
            />
          )}
        />
      );
    case "pdf":
      return (
        <Controller
          control={control}
          name={field.name}
          render={({ field: ctrl }) => (
            <PdfUpload
              label={field.label}
              hint={field.hint}
              required={field.required}
              subdir={field.subdir}
              value={ctrl.value}
              onChange={ctrl.onChange}
            />
          )}
        />
      );
    case "row": {
      // Side-by-side composite of two fields.
      return (
        <div className="grid grid-cols-2 gap-4">
          {field.fields.map((f) => (
            <FieldRenderer
              key={f.name}
              field={f}
              register={register}
              control={control}
              errors={errors}
              watch={watch}
              setValue={setValue}
            />
          ))}
        </div>
      );
    }
    default:
      return null;
  }
}
